export const TODO_SCHEMA_VERSION = 2;

export type TodoId = string;

export type TodoRecord = {
  id: TodoId;
  text: string;
  completed: boolean;
  createdAt: number;
  updatedAt: number;
  dueAt: number | null;
  deletedAt: number | null;
  origin: string;
};

export type TodoSnapshot = {
  schemaVersion: typeof TODO_SCHEMA_VERSION;
  deviceId: string;
  lastMutatedAt: number;
  todos: Record<TodoId, TodoRecord>;
};

export type SyncEnvelope =
  | {
      type: "hello";
      deviceId: string;
      timestamp: number;
    }
  | {
      type: "snapshot";
      snapshot: TodoSnapshot;
      senderPeerId?: string;
      timestamp: number;
    };
let idCounter = 0;

export function createId() {
  idCounter = (idCounter + 1) % Number.MAX_SAFE_INTEGER;

  const timePart = Date.now().toString(36);
  const counterPart = idCounter.toString(36).padStart(4, "0");
  const randomPart = Math.random().toString(36).slice(2, 12).padEnd(10, "0");

  return `${timePart}-${counterPart}-${randomPart}`;
}

function asTimestamp(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getDueSortValue(todo: TodoRecord) {
  return todo.dueAt ?? Number.POSITIVE_INFINITY;
}

export function normaliseTodoRecord(fallbackId: TodoId, value: unknown): TodoRecord | null {
  if (typeof value !== "object" || value == null) {
    return null;
  }

  const candidate = value as Partial<TodoRecord>;
  const id = typeof candidate.id === "string" ? candidate.id : fallbackId;
  const text = typeof candidate.text === "string" ? candidate.text.trim() : "";
  const createdAt = asTimestamp(candidate.createdAt);
  const updatedAt = asTimestamp(candidate.updatedAt);

  if (id.length === 0 || text.length === 0 || typeof candidate.completed !== "boolean") {
    return null;
  }

  const nextCreatedAt = createdAt ?? updatedAt ?? Date.now();
  const nextUpdatedAt = updatedAt ?? nextCreatedAt;

  return {
    id,
    text,
    completed: candidate.completed,
    createdAt: nextCreatedAt,
    updatedAt: nextUpdatedAt,
    dueAt: asTimestamp(candidate.dueAt),
    deletedAt: asTimestamp(candidate.deletedAt),
    origin:
      typeof candidate.origin === "string" && candidate.origin.length > 0
        ? candidate.origin
        : "unknown",
  };
}

export function compareTodoVersions(left: TodoRecord, right: TodoRecord) {
  if (left.updatedAt !== right.updatedAt) {
    return left.updatedAt - right.updatedAt;
  }

  if ((left.deletedAt ?? 0) !== (right.deletedAt ?? 0)) {
    return (left.deletedAt ?? 0) - (right.deletedAt ?? 0);
  }

  return left.origin.localeCompare(right.origin);
}

export function mergeTodoSnapshots(local: TodoSnapshot, remote: TodoSnapshot): TodoSnapshot {
  const mergedTodos: Record<TodoId, TodoRecord> = { ...local.todos };

  for (const [id, remoteTodo] of Object.entries(remote.todos)) {
    const nextRemoteTodo = normaliseTodoRecord(id, remoteTodo);
    if (nextRemoteTodo == null) {
      continue;
    }

    const localTodo = mergedTodos[id];

    if (localTodo == null || compareTodoVersions(localTodo, nextRemoteTodo) < 0) {
      mergedTodos[id] = nextRemoteTodo;
    }
  }

  return {
    schemaVersion: TODO_SCHEMA_VERSION,
    deviceId: local.deviceId,
    lastMutatedAt: Math.max(local.lastMutatedAt, remote.lastMutatedAt),
    todos: mergedTodos,
  };
}

export function toVisibleTodos(snapshot: TodoSnapshot) {
  return Object.entries(snapshot.todos)
    .map(([id, todo]) => normaliseTodoRecord(id, todo))
    .filter((todo): todo is TodoRecord => todo != null)
    .filter((todo) => todo.deletedAt == null)
    .sort((left, right) => {
      if (left.completed !== right.completed) {
        return Number(left.completed) - Number(right.completed);
      }

      if (getDueSortValue(left) !== getDueSortValue(right)) {
        return getDueSortValue(left) - getDueSortValue(right);
      }

      return right.updatedAt - left.updatedAt;
    });
}

export function encodeEnvelope(envelope: SyncEnvelope) {
  return new TextEncoder().encode(JSON.stringify(envelope));
}

export function decodeEnvelope(data: Uint8Array) {
  return JSON.parse(new TextDecoder().decode(data)) as SyncEnvelope;
}
